import { Component, inject, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { ToastModule } from 'primeng/toast';
import { AuthService } from './authentication/services/auth.service';
import { AppAnalyticsService } from './core/services/app-analytics.service';
import { NavigationHistoryService } from './core/services/navigation-history.service';
import { TranslationService } from './features/translations/services/translation.service';
import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, ToastModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  title = 'Buky';

  private transService = inject(TranslationService);
  private auth = inject(AuthService);
  private analytics = inject(AppAnalyticsService);
  // Eagerly created so it records navigation history from app startup.
  private navHistory = inject(NavigationHistoryService);


  constructor() {
    let language: string = this.auth.user()?.language?.isoCode2 || environment.languages[0];
    this.transService.getTranslations(language);
  }

  ngOnInit() {
    // Service worker is automatically registered by Angular PWA

    // Start first-party GA4 + FB Pixel page-view tracking (no-op unless the
    // build environment defines the IDs — i.e. production app.buky.me).
    this.analytics.init();

    // A restored session carries the permissions (and JWT claims) from its
    // last login; re-issue it so permissions added since then apply without
    // a manual logout/login. Skipped while impersonating — the refresh would
    // drop the impersonation claims. An expired token is left alone (the login
    // flow handles it as before).
    if (this.auth.user()?.authToken && !this.auth.imperosnating() && !this.auth.isTokenExpired()) {
      this.auth.refreshToken().subscribe({
        // The server turned the session down: drop it, or the error interceptor's
        // redirect to /login reloads straight into this refresh again (endless loop).
        error: (err) => {
          if (isAuthError(err)) this.auth.clearSession();
        },
      });
    }
  }
}

function isAuthError(err: any): boolean {
  const codes = [err?.extensions?.code, ...(err?.graphQLErrors ?? []).map((e: any) => e?.extensions?.code)];
  return err?.status === 401 || err?.networkError?.status === 401 || codes.includes('AUTH_NOT_AUTHORIZED');
}
